import { useCallback, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import type { UseFormHandleSubmit } from "react-hook-form";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import {
  createOAuthClient,
  editOAuthClient,
  type OAuthClientCreateResponse,
  type OAuthClientMutationRequest,
  type OAuthClientResponse,
  type OAuthClientSecretResponse,
  regenerateSecret,
} from "~/v2/api/oauth";
import { queryKeys } from "~/v2/api/queryKeys";
import type { SubmitAppFormValues } from "../SubmitApp.schema";
import type { SubmitAppModalState, SubmitAppMode } from "../SubmitApp.types";

interface UseSubmitAppSubmissionOptions {
  clientId?: string;
  handleSubmit: UseFormHandleSubmit<SubmitAppFormValues>;
  mode: SubmitAppMode;
  navigateToClient: (clientId: string) => void;
}

export const useSubmitAppSubmission = ({
  clientId,
  handleSubmit,
  mode,
  navigateToClient,
}: UseSubmitAppSubmissionOptions) => {
  const queryClient = useQueryClient();
  const [didAgree, setDidAgree] = useState(false);
  const [modal, setModal] = useState<SubmitAppModalState>({ kind: "closed" });
  const regenerateInFlight = useRef(false);

  const createMutation = useMutationRequest<
    OAuthClientCreateResponse,
    OAuthClientMutationRequest
  >(createOAuthClient, {
    onSuccess: async (client) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.oauth.createdClients(),
      });
      setModal({
        kind: "createSuccess",
        clientId: client.client_id,
        clientSecret: client.client_secret,
      });
    },
    onError: (error) => {
      setModal({
        kind: "error",
        action: "create",
        message: error.message,
      });
    },
  });

  const editRequest = useCallback(
    (payload: OAuthClientMutationRequest) => {
      if (!clientId) {
        return Promise.reject(new Error("Missing OAuth client ID."));
      }
      return editOAuthClient(clientId, payload);
    },
    [clientId],
  );

  const editMutation = useMutationRequest<
    OAuthClientResponse,
    OAuthClientMutationRequest
  >(editRequest, {
    onSuccess: async () => {
      if (!clientId) {
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.oauth.createdClients(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.oauth.clientById(clientId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.oauth.clientLogo(clientId),
        }),
      ]);
      setModal({ kind: "editSuccess", clientId });
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      setModal({
        kind: "error",
        action: "edit",
        message,
      });
    },
  });

  const regenerateMutation = useMutationRequest<
    OAuthClientSecretResponse,
    string
  >(regenerateSecret, {
    onSuccess: (credentials) => {
      setModal({
        kind: "regeneratedSecret",
        clientId: credentials.client_id,
        clientSecret: credentials.client_secret,
      });
    },
    onSettled: () => {
      regenerateInFlight.current = false;
    },
  });

  const submitValidValues = (formValues: SubmitAppFormValues) => {
    if (mode === "edit" && clientId) {
      editMutation.mutate(formValues);
      return;
    }

    createMutation.mutate(formValues);
  };

  const submitForm = handleSubmit(submitValidValues);

  const onSubmit: typeof submitForm = (event) => {
    if (!didAgree) {
      event?.preventDefault();
      return Promise.resolve();
    }
    return submitForm(event);
  };

  const onModalClose = () => {
    if (regenerateInFlight.current || regenerateMutation.isPending) {
      return;
    }
    if (modal.kind === "createSuccess" || modal.kind === "editSuccess") {
      navigateToClient(modal.clientId);
      return;
    }
    setModal({ kind: "closed" });
  };

  const onModalPrimary = () => {
    if (modal.kind === "credentials") {
      if (regenerateInFlight.current || regenerateMutation.isPending) {
        return;
      }
      regenerateInFlight.current = true;
      regenerateMutation.mutate(modal.clientId);
      return;
    }
    onModalClose();
  };

  const onViewCredentials = () => {
    if (clientId) {
      setModal({ kind: "credentials", clientId });
    }
  };

  return {
    didAgree,
    isRegenerating: regenerateMutation.isPending,
    isSubmitting: createMutation.isPending || editMutation.isPending,
    modal,
    onAgreementChange: setDidAgree,
    onModalClose,
    onModalPrimary,
    onSubmit,
    onViewCredentials,
  };
};
